// ── Import Batch Ledger (in-memory) ──
// Tracks import batches for idempotency + audit. Beta scope: process-local
// only (no DB table). A batch is a content-hashed slice of a single upload.
//
// Idempotency contract:
//   Primary key per imported job = `${sourceSystem}:${externalProjectId}`.
//   - If key already exists AND status === "approved" → skip (no-op).
//   - If key already exists AND status === "needs_review" → overwrite the
//     mapped fields, preserving operator edits when present (3-way diff is
//     a post-beta enhancement; current behavior is "new mapping wins").
//   - Same content hash re-uploaded → returns the prior batch unchanged.
//
// Anchor: mem://features/jobber-import-bridge (beta)

import type { SourceSystem } from "../types";

export interface ImportBatch {
  id: string;
  sourceSystem: SourceSystem;
  contentHash: string;
  createdAt: string;
  rowsTotal: number;
  rowsMapped: number;
  rowsSkippedDuplicate: number;
  notes?: string;
}

const batches = new Map<string, ImportBatch>();
const seenKeys = new Set<string>(); // `${sourceSystem}:${externalProjectId}`

export function jobKey(sourceSystem: SourceSystem, externalProjectId: string): string {
  return `${sourceSystem}:${externalProjectId}`;
}

export function hasImportedKey(key: string): boolean {
  return seenKeys.has(key);
}

export function markImportedKey(key: string): void {
  seenKeys.add(key);
}

export function getBatchByHash(hash: string): ImportBatch | undefined {
  for (const b of batches.values()) {
    if (b.contentHash === hash) return b;
  }
  return undefined;
}

export function createBatch(input: Omit<ImportBatch, "id" | "createdAt">): ImportBatch {
  const existing = getBatchByHash(input.contentHash);
  if (existing) return existing;
  const batch: ImportBatch = {
    ...input,
    id: `IMPORT-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
  };
  batches.set(batch.id, batch);
  return batch;
}

export function listBatches(): ImportBatch[] {
  return Array.from(batches.values()).sort(
    (a, b) => b.createdAt.localeCompare(a.createdAt),
  );
}

/** djb2 — cheap content hash for beta idempotency only (not security-grade). */
export function contentHash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(16);
}

/** Test/dev only — clear the in-memory ledger. */
export function __resetImportBatchLedger(): void {
  batches.clear();
  seenKeys.clear();
}
